import React from "react";

const LETTER_STYLES = {
  "A+": { bg: "#14532D", color: "#FFFFFF", label: "Viabilidad muy alta" },
  A:    { bg: "#15803D", color: "#FFFFFF", label: "Viabilidad alta" },
  B:    { bg: "#CA8A04", color: "#FFFFFF", label: "Viabilidad media" },
  C:    { bg: "#B45309", color: "#FFFFFF", label: "Viabilidad condicionada" },
  D:    { bg: "#991B1B", color: "#FFFFFF", label: "A revisar" },
};

/**
 * Píldora con la letra de viabilidad (A+/A/B/C/D) opcionalmente con score numérico.
 */
export default function ViabilidadLetra({ letra, score, size = "md", showScore = true }) {
  const cfg = LETTER_STYLES[letra] || { bg: "#374151", color: "#FFF", label: "—" };
  const pad = size === "lg" ? "px-3 py-1.5 text-sm" : "px-2 py-0.5 text-xs";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md font-semibold tabular-nums ${pad}`}
      style={{ background: cfg.bg, color: cfg.color }}
      title={cfg.label}
      data-testid={`viab-letra-${letra}`}
    >
      <span className="font-heading">{letra || "—"}</span>
      {showScore && score != null && (
        <span className="opacity-90 font-mono">{Number(score).toFixed(1)}</span>
      )}
    </span>
  );
}

/**
 * Tarjeta detallada para Ficha de Línea: muestra los 3 sub-scores con barras
 * horizontales y un breakdown del cálculo.
 */
export function ViabilidadScoreCard({ linea }) {
  const score = linea?.viabilidad_score ?? 0;
  const letra = linea?.viabilidad_letra || "—";
  const subs = linea?.viabilidad_subscores || {};
  const det = linea?.viabilidad_detalles || {};
  const cfg = LETTER_STYLES[letra] || LETTER_STYLES.B;
  const subscores = [
    {
      key: "eficiencia_energetica",
      label: "Eficiencia energética",
      hint: `kWh/km base = ${det.factor_kwh_km?.toFixed?.(2) ?? "—"}`,
      weight: 0.4,
      value: subs.eficiencia_energetica ?? 0,
    },
    {
      key: "longitud",
      label: "Longitud de línea",
      hint: `${det.longitud_km?.toFixed?.(1) ?? "—"} km comerciales`,
      weight: 0.3,
      value: subs.longitud ?? 0,
    },
    {
      key: "carga_operativa",
      label: "Carga operativa por vehículo",
      hint: `${det.kwh_por_vehiculo_dia ?? "—"} kWh/veh-día · batería ${det.bateria_utilizable_kwh ?? "—"} kWh (${det.tamano_vehiculo || "—"}) · ${det.n_recargas_dia ?? "—"} recarga(s)/día`,
      weight: 0.3,
      value: subs.carga_operativa ?? 0,
    },
  ];
  return (
    <div className="crtm-card p-5" data-testid="viabilidad-score-card">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-gray-900 text-[15px]">
            Score de viabilidad eléctrica
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Plan de servicio con tiempos de regulación = 0 · jornada típica
            {det.jornada_tipo_dia ? ` ${det.jornada_tipo_dia}` : ""} ({det.dias_anuales ?? "—"} días/año)
            · batería utilizable {det.bateria_utilizable_kwh ?? "—"} kWh
          </p>
        </div>
        <div
          className="rounded-xl px-5 py-3 text-center"
          style={{ background: cfg.bg + "12", border: `1px solid ${cfg.bg}33` }}
        >
          <div className="text-3xl font-heading font-semibold" style={{ color: cfg.bg }}>
            {letra}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5 tabular-nums">
            {Number(score).toFixed(2)} / 10
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {subscores.map((s) => (
          <div key={s.key} data-testid={`sub-${s.key}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-gray-700">
                {s.label} <span className="text-gray-400 font-normal">({Math.round(s.weight * 100)}%)</span>
              </span>
              <span className="tabular-nums text-gray-600">{s.value.toFixed(2)} / 10</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.max(2, (s.value / 10) * 100)}%`,
                  background: s.value >= 7 ? "#15803D" : s.value >= 4 ? "#CA8A04" : "#991B1B",
                }}
              />
            </div>
            <div className="text-[11px] text-gray-500 mt-1">{s.hint}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
        <Stat label="Vehículos mínimos" value={det.n_vehiculos_min ?? "—"} hint="layover = 0" />
        <Stat label="Salidas/día tipo" value={det.n_salidas_dia ?? "—"} />
        <Stat label="veh-h / día" value={det.veh_h_dia?.toFixed?.(1) ?? "—"} />
        <Stat
          label="Recargas/día"
          value={det.n_recargas_dia ?? "—"}
          hint={
            det.n_recargas_dia === 1
              ? "Solo carga nocturna"
              : det.n_recargas_dia === 2
              ? "1 recarga oportunidad"
              : det.n_recargas_dia >= 3
              ? `${(det.n_recargas_dia ?? 1) - 1} recargas oportunidad`
              : "—"
          }
        />
      </div>

      <div className="mt-3 text-[11px] text-gray-400">
        Score final = 0,4 × F1 + 0,3 × F2 + 0,3 × F3 ·{" "}
        A+ ≥ 9 · A ≥ 7 · B ≥ 5 · C ≥ 3 · D &lt; 3
      </div>
    </div>
  );
}

const Stat = ({ label, value, hint }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
    <div className="text-sm font-semibold text-gray-900 mt-0.5 tabular-nums">{value}</div>
    {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
  </div>
);
